/**
 * Every confirm string the member role and membership actions ship on web, in
 * one pure function, so the members pane and any later surface cannot grow two
 * different sentences for the same action.
 *
 * THE COPY IS NOT NEW AND MUST NOT BE RE-DERIVED. It is the same set
 * `apps/mobile/src/pools/memberActionConfirm.ts` ships, word for word, because
 * the two platforms are one product and a fan who reads a different sentence
 * on the web than on their phone has been told two different things about the
 * same button. Four of the five come from the design pack's "Destructive
 * confirmations" table in `.agents/design-prompts/26-pool-members.md`; the
 * `removeFromPool` body is owner answer Q169's correction of it, and
 * `makeAdmin`/`makeMember` were written for the shipped server, which is why
 * the `makeAdmin` body says an Admin cannot change roles or hand the pool over.
 * `memberActionConfirm.test.ts` reads the mobile file and fails if the two ever
 * drift.
 *
 * The two apps hold a copy each rather than importing one module, which is the
 * same shape `memberOverflowMenu.ts` already takes in both trees: there is no
 * package both `apps/app` and `apps/mobile` depend on that could hold shared
 * product copy, and inventing one is a bigger decision than this card owns. The
 * drift test is what makes the duplication safe.
 */

import type { typeConfirmDestructiveInput } from "@sportsmet/ui";

export type typeMemberActionId =
  | "makeAdmin"
  | "makeMember"
  | "suspend"
  | "unsuspend"
  | "removeFromPool"
  | "leavePool";

/**
 * The confirm for one action, or `null` when the action needs no confirm.
 * `null` is returned only for "unsuspend": restoring someone's access destroys
 * nothing, and `destructive-confirmation.mdc` scopes the confirm to delete,
 * remove-from-a-list, leave, and discarding saved work.
 */
export function memberActionConfirm(input: {
  actionId: typeMemberActionId;
  targetDisplayName: string;
}): typeConfirmDestructiveInput | null {
  // A blank name must never produce "Remove  from the pool?".
  const name = input.targetDisplayName.trim() || "this member";

  // A switch, not a Record lookup, so a new typeMemberActionId member is a
  // compile error rather than a silent undefined.
  switch (input.actionId) {
    case "makeAdmin":
      return {
        title: `Make ${name} an Admin?`,
        body: "Admins can approve, suspend, and remove members. They can't change roles or hand over the pool.",
        confirmLabel: "Make Admin",
      };
    case "makeMember":
      return {
        title: `Make ${name} a Member?`,
        body: "They'll lose the Admin tools: approving, suspending, and removing members.",
        confirmLabel: "Make Member",
      };
    case "suspend":
      return {
        title: `Suspend ${name}?`,
        body: "They keep their entry but can't make picks until you unsuspend them.",
        confirmLabel: "Suspend",
      };
    case "unsuspend":
      return null;
    case "removeFromPool":
      return {
        title: `Remove ${name} from the pool?`,
        body: "Their run in this contest ends right now, the same as running out of lives. Their picks and their place in the standings stay.",
        confirmLabel: "Remove",
      };
    case "leavePool":
      // No name is interpolated: the person leaving is the reader.
      return {
        title: "Leave this pool?",
        body: "You'll lose your entry and your picks for this season, and any alert settings you set for this pool.",
        confirmLabel: "Leave pool",
      };
  }
}

/**
 * The confirm the hand-over picker raises once a successor has been picked.
 *
 * Separate from the table above because it names a person the fan just chose
 * rather than the row they opened a menu on, and because its body depends on
 * which of the two entry points they came through. `thenLeave` is that
 * difference and it is the highest-consequence flag in this feature: the
 * Commissioner's own `Leave pool` transfers and then leaves, while the Settings
 * Danger zone transfers and the old Commissioner stays. Both bodies are the
 * mobile screen's, word for word.
 *
 * THE STAY-IN BODY SAYS "Admin", NOT "Member", AND THAT IS A CORRECTION.
 * Both platforms shipped "You stay in as a Member." That is not what happens:
 * `transferOwnership` in `services/pool-api/src/repo/poolMembers.ts` sets the
 * outgoing owner to `poolRole: "admin"` in the same transaction that promotes
 * the new one. A destructive confirm has to state the real consequence
 * (`destructive-confirmation.mdc`), and ISSUE-817 R5 says the old Commissioner
 * "keeps their membership at the new role", so the sentence named the wrong
 * role. Corrected here and in `apps/mobile/app/(tabs)/pools/hand-over.tsx` in
 * one change, with the drift test below holding the two in step.
 */
export function handOverConfirm(input: {
  targetDisplayName: string;
  thenLeave: boolean;
}): typeConfirmDestructiveInput {
  const name = input.targetDisplayName.trim() || "this member";
  return {
    title: `Make ${name} the Commissioner?`,
    body: input.thenLeave
      ? "They take over running the pool, and you leave right after. You'll lose your entry and your picks for this season."
      : "They take over running the pool. You stay in as an Admin.",
    confirmLabel: "Hand over",
  };
}
